/**
 * `hooks` — outbound site hooks from a portal-hosted widget to the client's own systems
 * (`.docs/46-blog-and-forms.md` §5).
 *
 * The blog needs a rebuild trigger (a static site pulls its posts at build time, so a
 * publish has to start a build) and forms will want Zapier and CRMs, so one module
 * serves both: a **site hook** is a GitHub workflow run (`workflow_dispatch`), a GitHub
 * `repository_dispatch`, or a signed HTTP POST in the Standard Webhooks shape, subscribed
 * to some of the events below.
 *
 * Everything a hook's rules depend on is here, because the API validates with these
 * objects and the SPA's Connect page resolves its forms with the same ones.
 */
import { z } from 'zod';
import { DateOnlySchema, Rfc3339Schema } from './primitives.js';
import { WidgetRefSchema } from './refs.js';
/* ------------------------------------------------------------------- the events */
/**
 * Every event a hook may subscribe to, and nothing else.
 *
 * `post.*` are the blog's: a first publication, an update to a live post, an
 * unpublication, and `post.live` — the moment a **scheduled** post's time arrives, which
 * no write causes (session file decision 8: `scheduled` is derived, so a sweep announces
 * it). `submission.received` is the forms module's, fired for a clean lead only.
 */
export const SITE_HOOK_EVENTS = [
    'post.published',
    'post.updated',
    'post.unpublished',
    'post.live',
    'submission.received',
];
export const SiteHookEventSchema = z
    .enum(SITE_HOOK_EVENTS)
    .describe('An event a hook can be told about: post.published (a post’s first publication), ' +
    'post.updated (a live post changed), post.unpublished, post.live (a scheduled post’s ' +
    'time arrived), or submission.received (a form took a clean submission).');
/**
 * Which events each hosted surface fires.
 *
 * A hook subscribed to an event its widget never fires would wait for ever and look
 * healthy doing it, so a subscription is checked against this rather than against the
 * whole list. It is also what makes a GitHub hook on a forms widget impossible without a
 * rule of its own: the only event a form fires is the one a rebuild trigger may not carry.
 */
export const SITE_HOOK_EVENTS_BY_SURFACE = {
    blog: ['post.published', 'post.updated', 'post.unpublished', 'post.live'],
    forms: ['submission.received'],
};
/**
 * The events a GitHub hook — either kind — may not carry.
 *
 * A GitHub hook is a rebuild trigger. A `repository_dispatch`'s `client_payload` lands in
 * a workflow run's event log, readable by anybody with read access to the repository; a
 * `workflow_dispatch` carries no payload at all, so a submission would be a build for
 * nothing. A lead's name, address and message have no business in a repository either
 * way — so a GitHub hook may not subscribe to a submission, and the database refuses it
 * too.
 */
export const GITHUB_FORBIDDEN_EVENTS = ['submission.received'];
/**
 * The one internal event, sent by "Send a test delivery" and never subscribable.
 *
 * Not a member of `SITE_HOOK_EVENTS`, so no hook can ask for it and no domain write can
 * fire it; a delivery row may carry it, and that is the only place it appears.
 */
export const SITE_HOOK_PING = 'ping';
/** What a delivery row's `event` may be: a subscribable event, or the test ping. */
export const SITE_HOOK_DELIVERY_EVENTS = [...SITE_HOOK_EVENTS, SITE_HOOK_PING];
/* -------------------------------------------------------------------- the kinds */
/**
 * `github_workflow` — one `POST /repos/{owner}/{repo}/actions/workflows/{workflow}/dispatches`
 * per batch of events, which starts a run of one workflow on one branch. Its fine-grained
 * token needs **Actions: write** and nothing else: it can start a run and cannot change a
 * line of the repository, which is why it is the recommended kind (session 46b).
 * `github_dispatch` — one `POST /repos/{owner}/{repo}/dispatches` per batch, whose token
 * needs **Contents: write** — a token that could push code, and on a site that deploys
 * every push to its main branch, code that goes live. Both tokens live at most a year.
 * `http` — one signed POST per event to a URL the client owns, in the Standard Webhooks
 * shape, signed with a secret the portal generates.
 */
export const SITE_HOOK_KINDS = ['github_dispatch', 'github_workflow', 'http'];
/** The two kinds that call GitHub with a client's token. Everything GitHub-only reads this. */
export const GITHUB_SITE_HOOK_KINDS = ['github_dispatch', 'github_workflow'];
/** Whether a hook calls GitHub — its secret a token, its target `owner/repo`. */
export function isGithubHookKind(kind) {
    return GITHUB_SITE_HOOK_KINDS.includes(kind);
}
export const SiteHookKindSchema = z
    .enum(SITE_HOOK_KINDS)
    .describe('How the hook calls out: github_workflow (runs one GitHub Actions workflow through ' +
    'workflow_dispatch — recommended, because its token needs only Actions: write), ' +
    'github_dispatch (a repository_dispatch, whose token needs Contents: write) or http ' +
    '(a signed POST to a URL, for Zapier, a CRM or a server of the client’s own).');
/** pending → sent, or pending → failed. A failed one can be sent again by a person. */
export const SITE_HOOK_DELIVERY_STATUSES = ['pending', 'sent', 'failed'];
export const SiteHookDeliveryStatusSchema = z
    .enum(SITE_HOOK_DELIVERY_STATUSES)
    .describe('pending (waiting to be sent, or waiting to be tried again), sent (the target answered ' +
    '2xx) or failed (every attempt was refused, or the hook was switched off first).');
/* ------------------------------------------------------------------- the target */
/**
 * `owner/repo`, by GitHub's own naming rules.
 *
 * An owner is 1–39 letters, digits and single hyphens, neither first nor last; a
 * repository is 1–100 of letters, digits, `.`, `_` and `-`, is not `.` or `..`, and does
 * not end in `.git`. Checked here so that a typo is a refusal at creation rather than a
 * 404 from GitHub on the first publish.
 */
export const GITHUB_REPO_PATTERN = /^[A-Za-z0-9](?:[A-Za-z0-9]|-(?=[A-Za-z0-9])){0,38}\/(?!\.{1,2}$)[A-Za-z0-9._-]{1,100}(?<!\.git)$/;
export const GithubRepoSchema = z
    .string()
    .trim()
    .regex(GITHUB_REPO_PATTERN, 'A GitHub repository is owner/name — "FeritMelih/soft-tech-landing".')
    .describe('The GitHub repository the hook calls, owner/name.');
/** The longest URL a hook may call. Longer than any real endpoint; shorter than abuse. */
export const SITE_HOOK_URL_MAX = 2_000;
/**
 * An `https://` URL with a host and no credentials in it.
 *
 * `user:password@` is refused because a secret in a URL is a secret printed on every
 * screen that shows the hook; a signing secret is how an http hook authenticates. This
 * says nothing about **where** the host is — a private address is refused at delivery
 * time, by DNS, immediately before connecting, because a name that resolves publicly
 * today can resolve to 10.0.0.1 tomorrow.
 */
export const HookUrlSchema = z
    .string()
    .trim()
    .max(SITE_HOOK_URL_MAX, `A hook URL is at most ${String(SITE_HOOK_URL_MAX)} characters.`)
    .pipe(z.url({ protocol: /^https$/, error: 'A hook URL is an https:// URL with a host.' }))
    // `packages/shared` has no `URL` type (no DOM lib, no Node types), so the authority is
    // read by pattern: anything before the first `/`, `?` or `#` after the scheme that
    // carries an `@` is a user name, a password, or both.
    .refine((value) => !/^https:\/\/[^/?#]*@/i.test(value), 'A hook URL carries no user name or password; the signing secret is how it is trusted.')
    .describe('The https:// URL the signed POST goes to.');
/** GitHub's `event_type`: what the workflow's `on.repository_dispatch.types` matches. */
export const GITHUB_EVENT_TYPE_PATTERN = /^[a-zA-Z0-9_.-]{1,100}$/;
/** The `event_type` a GitHub hook sends when nobody names one. */
export const DEFAULT_GITHUB_EVENT_TYPE = 'fes_blog_changed';
export const GithubEventTypeSchema = z
    .string()
    .regex(GITHUB_EVENT_TYPE_PATTERN, 'An event type is 1 to 100 letters, digits, dots, hyphens and underscores.')
    .describe(`The event_type the dispatch carries — what the workflow's repository_dispatch types ` +
    `match. "${DEFAULT_GITHUB_EVENT_TYPE}" when left out. github_dispatch only.`);
/**
 * A workflow as GitHub's `workflow_dispatch` endpoint names one: its file name under
 * `.github/workflows` — `deploy.yml` — or its numeric id.
 *
 * The file name is letters, digits, `.`, `_` and `-` ending in `.yml` or `.yaml`, which
 * is every name GitHub's own UI makes and keeps a `/` (a second path segment) or a `?`
 * out of the URL it lands in. The id is what `GET /repos/{owner}/{repo}/actions/workflows`
 * answers, a positive integer.
 */
export const GITHUB_WORKFLOW_FILE_PATTERN = /^[A-Za-z0-9_.-]{1,100}\.ya?ml$/;
export const GITHUB_WORKFLOW_ID_PATTERN = /^[1-9][0-9]{0,19}$/;
export const GithubWorkflowSchema = z
    .string()
    .trim()
    .refine((value) => GITHUB_WORKFLOW_FILE_PATTERN.test(value) || GITHUB_WORKFLOW_ID_PATTERN.test(value), 'A workflow is its file name under .github/workflows — "deploy.yml" — or its numeric id.')
    .describe('The workflow a github_workflow hook runs: its file name under .github/workflows ' +
    '("deploy.yml") or its numeric id. The workflow must listen to workflow_dispatch.');
/**
 * A branch, by the rules of `git check-ref-format` that a branch name typed into a form
 * can break: letters, digits, `.`, `_`, `/` and `-`, at most 100; no `..` and no `//`;
 * not starting with `/` or `-`; not ending in `/` or `.lock`.
 */
export const GIT_BRANCH_PATTERN = /^(?![/-])(?!.*\.\.)(?!.*\/\/)(?!.*\/$)(?!.*\.lock$)[A-Za-z0-9._/-]{1,100}$/;
/** The branch a workflow hook runs on when nobody names one. */
export const DEFAULT_GITHUB_WORKFLOW_REF = 'main';
export const GitRefSchema = z
    .string()
    .trim()
    .regex(GIT_BRANCH_PATTERN, 'A branch is up to 100 letters, digits, dots, slashes, hyphens and underscores — ' +
    '"main" — with no "..", not starting with "/" or "-", not ending in "/" or ".lock".')
    .describe(`The branch a github_workflow hook runs the workflow on — the one the site deploys ` +
    `from. "${DEFAULT_GITHUB_WORKFLOW_REF}" when left out.`);
/**
 * A GitHub token, as GitHub issues them: `github_pat_…`, `ghp_…`, `ghs_…`.
 *
 * Letters, digits and underscores only, which is what every GitHub token format is —
 * and which is also what keeps a pasted newline or a space out of an `Authorization`
 * header.
 */
export const GITHUB_TOKEN_PATTERN = /^[A-Za-z0-9_]{20,255}$/;
/**
 * How long before a GitHub token's expiry the portal starts warning about it: a fortnight.
 *
 * A fine-grained token lives at most a year, and the first sign of one expiring is
 * otherwise a site that silently stops rebuilding when a post is published. Two readers,
 * one number: the studio's morning digest (`apps/api/src/jobs/digest.ts`
 * `expiringSiteHookTokens`) and the Connect page's hook list, which marks the token.
 */
export const SITE_HOOK_EXPIRY_WARNING_DAYS = 14;
export const GithubTokenSchema = z
    .string()
    .trim()
    .regex(GITHUB_TOKEN_PATTERN, 'That does not look like a GitHub token (github_pat_…).')
    .describe('A fine-grained GitHub token for the one repository, expiring within a year: Actions: ' +
    'write and nothing else for github_workflow, Contents: write and nothing else for ' +
    'github_dispatch. Write-only: it is encrypted at rest and never shown again.');
export const SiteHookDescriptionSchema = z
    .string()
    .trim()
    .min(1)
    .max(200)
    .describe('A note for people — "Rebuilds softtech.madebyfareast.com". At most 200 characters.');
/* --------------------------------------------------------------------- the views */
export const SiteHookSchema = z
    .object({
    id: z.uuid().describe('The hook’s id. Hooks are configuration and have no human reference.'),
    widget: WidgetRefSchema,
    kind: SiteHookKindSchema,
    target: z
        .string()
        .describe('owner/name for either GitHub kind. For an http hook the URL masked — its scheme, ' +
        'its host and the last four characters of its path and query, ' +
        '"https://hooks.zapier.com/…xyz9" — because a catch URL is often the credential ' +
        'itself. The whole URL is answered once, as target_url, when the hook is made.'),
    event_type: z
        .string()
        .nullable()
        .describe('The event_type a github_dispatch hook’s repository_dispatch carries; null for ' +
        'every other kind.'),
    workflow: z
        .string()
        .nullable()
        .describe('The workflow a github_workflow hook runs — its file name or id; null for every ' +
        'other kind.'),
    ref: z
        .string()
        .nullable()
        .describe('The branch a github_workflow hook runs the workflow on; null for every other kind.'),
    events: z.array(SiteHookEventSchema).describe('The events this hook is told about.'),
    active: z.boolean().describe('False while it is switched off: nothing is queued for it.'),
    description: z.string().nullable().describe('A note for people, or null.'),
    secret_expires_on: DateOnlySchema.nullable().describe('The day the GitHub token stops working, as GitHub showed it when it was made; the ' +
        'portal warns inside 14 days. Null when nobody said, and always null for http.'),
    created_at: Rfc3339Schema,
    updated_at: Rfc3339Schema,
})
    .describe('One outbound site hook. Its secret is never part of it: a GitHub token is write-only, ' +
    'and an http signing secret is shown once, when it is made.');
/**
 * A hook together with the signing secret it was just given, if any.
 *
 * `signing_secret` is the `whsec_…` value an http hook signs with, present exactly once —
 * on the answer that created or rotated it — and null on every other answer, and always
 * null for a GitHub hook, whose token the portal never hands back. `target_url` is an
 * http hook's whole URL, present exactly once, on the answer that created it: every other
 * answer masks it (`hook.target`), because a catch URL often is the credential.
 */
export const SiteHookWithSecretSchema = z
    .object({
    hook: SiteHookSchema,
    signing_secret: z
        .string()
        .nullable()
        .describe('The http hook’s signing secret, whsec_…, shown this once and never again — copy it ' +
        'into the receiver now. Null for a GitHub hook and when the secret did not change.'),
    target_url: z
        .string()
        .nullable()
        .describe('The http hook’s whole URL, answered this once, when it is made; hook.target and ' +
        'every later answer mask it. Null for a GitHub hook, whose target is not masked, ' +
        'and on every answer but the one that made the hook.'),
})
    .describe('A hook, the signing secret it was just given if it was given one, and its whole URL ' +
    'if it was just made.');
export const SiteHookDeliverySchema = z
    .object({
    id: z
        .uuid()
        .describe('The delivery’s id — the webhook-id header of an http delivery, and the delivery ' +
        'field of a repository_dispatch it opened. A workflow run carries no payload.'),
    hook_id: z.uuid().describe('The hook it was queued for.'),
    event: z
        .enum(SITE_HOOK_DELIVERY_EVENTS)
        .describe('What happened, or "ping" for a test delivery.'),
    status: SiteHookDeliveryStatusSchema,
    attempts: z.int().describe('How many times it has been sent so far.'),
    response_status: z
        .int()
        .nullable()
        .describe('The HTTP status the target last answered with, or null when none answered.'),
    error: z.string().nullable().describe('Why the last attempt did not count as sent, or null.'),
    payload: z
        .object({
        event: z.string().describe('The event, again.'),
        occurred_at: Rfc3339Schema.describe('When it happened.'),
        data: z
            .record(z.string(), z.unknown())
            .describe('What the module that fired it said about it — a post, a submission.'),
    })
        .describe('What was queued: the event, when, and what it was about.'),
    created_at: Rfc3339Schema,
    last_attempt_at: Rfc3339Schema.nullable().describe('When it was last sent, or null.'),
    next_attempt_at: Rfc3339Schema.nullable().describe('When it will be sent next, while it is pending; null once it is sent or failed.'),
    sent_at: Rfc3339Schema.nullable().describe('When the target accepted it, or null.'),
})
    .describe('One event queued for one hook, and what became of it.');
/* -------------------------------------------------------------------- the inputs */
const EVENTS_FIELD = z
    .array(SiteHookEventSchema)
    .min(1, 'Subscribe the hook to at least one event.')
    .max(SITE_HOOK_EVENTS.length)
    .refine((events) => new Set(events).size === events.length, 'Name each event once.')
    .describe('The events this hook is told about. At least one; each named once.');
/**
 * The fields of a new hook, flat.
 *
 * One object with a `kind` rather than a discriminated union of three, and deliberately:
 * a tool's input is a JSON Schema whose top level is one `object` (a union becomes an
 * `anyOf` there, which an MCP client is not obliged to read), the action spreads these
 * fields beside its `widget` (`hooks/actions.ts`), and a form draws one shape whose boxes
 * come and go with the kind. The rules that join the fields are `checkCreateSiteHook`,
 * one sentence per field, applied by `CreateSiteHookSchema` and by the action:
 *
 *   | kind              | target       | token    | event_type | workflow | ref      | expiry |
 *   |-------------------|--------------|----------|------------|----------|----------|--------|
 *   | `github_workflow` | `owner/repo` | required | refused    | required | optional | yes    |
 *   | `github_dispatch` | `owner/repo` | required | optional   | refused  | refused  | yes    |
 *   | `http`            | `https://…`  | refused  | refused    | refused  | refused  | no     |
 *
 * and neither GitHub kind may hear `submission.received` (`GITHUB_FORBIDDEN_EVENTS`).
 */
export const CreateSiteHookFields = z.strictObject({
    kind: SiteHookKindSchema,
    target: z
        .string()
        .trim()
        .min(1)
        .max(SITE_HOOK_URL_MAX)
        .describe('Where it calls: owner/name of the GitHub repository for github_workflow and ' +
        'github_dispatch, or the https:// URL for http.'),
    events: EVENTS_FIELD,
    token: GithubTokenSchema.optional().describe('Required for both GitHub kinds: a fine-grained token for the one repository, expiring ' +
        'within a year — Actions: write only for github_workflow, Contents: write only for ' +
        'github_dispatch. Encrypted at rest and never shown again. Left out for http, whose ' +
        'signing secret the portal generates and shows once.'),
    event_type: GithubEventTypeSchema.optional(),
    workflow: GithubWorkflowSchema.optional().describe('Required for github_workflow, refused for every other kind: the workflow to run — its ' +
        'file name under .github/workflows ("deploy.yml") or its numeric id. It must listen ' +
        'to workflow_dispatch.'),
    ref: GitRefSchema.optional().describe(`For github_workflow only: the branch to run the workflow on. ` +
        `"${DEFAULT_GITHUB_WORKFLOW_REF}" when left out.`),
    secret_expires_on: DateOnlySchema.optional().describe('For either GitHub kind: the day the token expires, as GitHub showed it. The portal ' +
        'warns inside 14 days. Left out for http.'),
    description: SiteHookDescriptionSchema.optional(),
    active: z
        .boolean()
        .default(true)
        .describe('False to make it switched off from the start. True when left out.'),
});
/**
 * The cross-field rules of a new hook, as issues on the field each is about.
 *
 * Exported so the action input — which is the same fields plus the widget — applies the
 * very same function rather than a second copy of the rules.
 */
export function checkCreateSiteHook(input, ctx) {
    const refuse = (field, message) => {
        if (input[field] !== undefined)
            ctx.addIssue({ code: 'custom', path: [field], message });
    };
    if (isGithubHookKind(input.kind)) {
        const workflow = input.kind === 'github_workflow';
        if (!GITHUB_REPO_PATTERN.test(input.target)) {
            ctx.addIssue({
                code: 'custom',
                path: ['target'],
                message: 'A GitHub hook’s target is owner/name — "FeritMelih/soft-tech-landing".',
            });
        }
        if (input.token === undefined) {
            ctx.addIssue({
                code: 'custom',
                path: ['token'],
                message: workflow
                    ? 'A GitHub hook needs a token: a fine-grained one, Actions: write only.'
                    : 'A GitHub hook needs a token: a fine-grained one, Contents: write only.',
            });
        }
        for (const event of input.events.filter((one) => GITHUB_FORBIDDEN_EVENTS.includes(one))) {
            ctx.addIssue({
                code: 'custom',
                path: ['events'],
                message: `A GitHub hook cannot be told about ${event}: a build trigger is readable by ` +
                    'anybody with read access to the repository, and a lead’s details do not belong ' +
                    'there. Use an http hook for submissions.',
            });
        }
        if (workflow) {
            if (input.workflow === undefined) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['workflow'],
                    message: 'A workflow hook names the workflow it runs: its file name under ' +
                        '.github/workflows — "deploy.yml" — or its numeric id.',
                });
            }
            refuse('event_type', 'A workflow hook takes no event type — workflow_dispatch has none; that is a ' +
                'repository_dispatch’s (github_dispatch).');
        }
        else {
            refuse('workflow', 'A repository_dispatch hook takes no workflow — that is a workflow hook’s ' +
                '(github_workflow), which runs one workflow by name.');
            refuse('ref', 'A repository_dispatch hook takes no branch — GitHub runs it on the default branch. ' +
                'A workflow hook (github_workflow) runs on a branch you name.');
        }
        return;
    }
    if (!HookUrlSchema.safeParse(input.target).success) {
        ctx.addIssue({
            code: 'custom',
            path: ['target'],
            message: 'An http hook’s target is an https:// URL with a host and no user name or password, ' +
                `at most ${String(SITE_HOOK_URL_MAX)} characters.`,
        });
    }
    for (const [field, words] of [
        ['token', 'token — its signing secret is generated by the portal and shown once'],
        ['event_type', 'event type — that is GitHub’s'],
        ['workflow', 'workflow — that is GitHub’s'],
        ['ref', 'branch — that is GitHub’s'],
        ['secret_expires_on', 'expiry — the signing secret does not expire'],
    ]) {
        refuse(field, `An http hook takes no ${words}.`);
    }
}
export const CreateSiteHookSchema = CreateSiteHookFields.superRefine(checkCreateSiteHook);
/**
 * A change to a hook. Every field is optional and what is left out is left alone.
 *
 * The secret is replaced, never read: `token` replaces a GitHub hook's token, and
 * `rotate_secret` gives an http hook a new signing secret, answered once. A workflow
 * hook may also be pointed at another workflow or branch. Which fields apply depends on
 * the hook's kind, which the body does not carry, so the service is what refuses the
 * wrong one — by name. The kind and the target never change: a different one is a
 * different hook.
 */
export const UpdateSiteHookFields = z.strictObject({
    events: EVENTS_FIELD.optional(),
    active: z.boolean().optional().describe('False switches it off; true switches it back on.'),
    description: SiteHookDescriptionSchema.nullable()
        .optional()
        .describe('A new note, or null to clear it.'),
    secret_expires_on: DateOnlySchema.nullable()
        .optional()
        .describe('GitHub only: the day the token expires, or null when nobody knows.'),
    token: GithubTokenSchema.optional().describe('GitHub only: a new token, replacing the old one. Write-only, like the first.'),
    workflow: GithubWorkflowSchema.optional().describe('github_workflow only: another workflow to run — its file name or numeric id.'),
    ref: GitRefSchema.optional().describe('github_workflow only: another branch to run the workflow on.'),
    rotate_secret: z
        .literal(true)
        .optional()
        .describe('http only: true to replace the signing secret. The new one is answered once; the old ' +
        'one stops being used at once, so put the new one in the receiver first.'),
});
/** The query of a delivery log page. */
export const SiteHookDeliveriesQuerySchema = z.strictObject({
    hook: z.uuid().optional().describe('Only this hook’s deliveries. Left out, every hook’s.'),
    status: SiteHookDeliveryStatusSchema.optional().describe('Only deliveries in this state. Left out, all three.'),
});
//# sourceMappingURL=hooks.js.map